import React, {useState} from 'react';
import Toggle from "components/Form/Toggle";
import Field from "components/Form/Field";
import {FormikHelpers} from "formik";
import {LocationFilter as LocationFilterSpec} from "types/AudienceCriteria";
import {useOperator} from "context/OperatorContext";
import {useQuery} from "react-query";
import ngage from "../../../../services/ngage";
import IconName from "../../../../types/IconName";
import MultiSelector from "../../MultiSelector";
import {Item} from "../../MultiSelector/MultiSelector";

// Types ----

enum LocationMode {ON = 'on', OFF = 'off'}

enum SiteMode {ALL, SELECTED}

type LocationFilterProps = {
  formik: FormikHelpers<any>
  groupIndex: number
  filter?: LocationFilterSpec | null
}

// Component ----

function LocationFilter({formik, groupIndex, filter}: LocationFilterProps) {
  const {termFor} = useOperator();

  const [selectedLocationMode, setSelectedLocationMode] = useState<LocationMode>(
    filter?.mode === LocationMode.OFF? LocationMode.OFF : LocationMode.ON
  );
  const [selectedSiteMode, setSelectedSiteMode] = useState<SiteMode>(
    filter?.sites && filter.sites.length? SiteMode.SELECTED : SiteMode.ALL
  );

  const {data: sites, isLoading: isLoadingSites} = useQuery(
    ['sites.all', {page: 1, perPage: 100}],
    () => ngage.sites.filtered({page: 1, per_page: 1000})
  )

  const siteOptions = sites?.data.map(site => ({
    type: 'site',
    id: site.id,
    name: site.name,
    description: site.address?.city,
    icon: IconName.OFFICE_BUILDING
  })) ?? [];

  function toggleFilter(checked: boolean): void {
    setSelectedLocationMode(checked? LocationMode.ON : LocationMode.OFF);

    void formik.setFieldValue(
      `criteria.groups.${groupIndex}.filters.location`,
      checked ? {mode: LocationMode.ON, sites: []} : undefined
    )
  }

  function selectLocationMode(mode: LocationMode): void {
    if(mode !== selectedLocationMode) {
      setSelectedLocationMode(mode)

      void formik.setFieldValue(
        `criteria.groups.${groupIndex}.filters.location.mode`,
        mode
      )
    }
  }

  function selectSiteMode(mode: SiteMode): void {
    setSelectedSiteMode(Number(mode));
    void formik.setFieldValue(
      `criteria.groups.${groupIndex}.filters.location.sites`,
      []
    );
  }

  function selectSiteOptions(options?: Array<Item>): void {
    if(options === undefined) return;

    const selectedSites = options.map(option => ({site_id: option.id}))

    void formik.setFieldValue(
      `criteria.groups.${groupIndex}.filters.location.sites`,
      selectedSites
    );
  }

  return (
    <div>
      <div className="flex">
        <Toggle
          label={`Current location${!!filter ? ' is' : '...'}`}
          checked={!!filter}
          onChange={toggleFilter}
          loading={isLoadingSites}
          disabled={isLoadingSites}
        />
      </div>

      {!!filter &&
        <div className="mt-2 pl-14 sm:flex">
          <div className="w-32 mt-2 sm:mt-0">
            <Field
              as="select"
              disabled={!filter}
              id={`groups.${groupIndex}.filters.location.mode`}
              value={selectedLocationMode}
              onChange={(e: any) => selectLocationMode(e.target.value)}
            >
              <option value={LocationMode.ON}>on</option>
              <option value={LocationMode.OFF}>off</option>
            </Field>
          </div>

          <div className="w-48 mt-2 sm:mt-0 sm:ml-3">
            <Field
              as="select"
              disabled={!filter}
              id={`groups.${groupIndex}.filters.location.sites-mode`}
              value={selectedSiteMode}
              onChange={(e: any) => selectSiteMode(e.target.value)}
            >
              <option value={SiteMode.ALL}>
                {selectedLocationMode === LocationMode.ON? `any ${termFor('site')}` : `all ${termFor('sites')}`}
              </option>
              <option value={SiteMode.SELECTED}>{`selected ${termFor('sites')}...`}</option>
            </Field>
          </div>

          {selectedSiteMode === SiteMode.SELECTED &&
            <div className="w-96 mt-2 sm:mt-0 sm:ml-3">
              <MultiSelector
                isLoading={isLoadingSites}
                selectedIds={filter.sites?.map(site => site.site_id) ?? []}
                items={siteOptions}
                onChange={selectSiteOptions}
              />
            </div>
          }
        </div>
      }
    </div>
  );
}

// Export ----

export default LocationFilter;